import debounce from 'lodash/debounce'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import {
  FilterAccordion,
  percentageMask,
  TextField,
  TextFieldProps,
  useOnClickOutside,
} from '@avantstay/backoffice-core'

import * as S from '@new/__global/components/Filters/Filters.styles'

import { FiltersSharedProps } from './Filters.types'

interface OccupancyRangeFilterFilterProps
  extends FiltersSharedProps,
    Omit<TextFieldProps, 'onChange'> {
  title: string
  onChange: (value: string | undefined, filterKey: string) => void
  fromValue?: string
  toValue?: string
  fromLabel?: string
  toLabel?: string
}

export const OccupancyRangeFilter: FC<OccupancyRangeFilterFilterProps> = ({
  title,
  isSidebarFilter,
  onChange,
  isAccordionOpen,
  fromValue,
  toValue,
  ...textFieldProps
}) => {
  const [fromLocalValue, setFromLocalValue] = useState<string | undefined>(fromValue)
  const [toLocalValue, setToLocalValue] = useState<string | undefined>(toValue)

  const wrapperRef = useRef<HTMLDivElement>(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInputOnChange = useCallback(
    debounce((filterKey: string, value?: string) => {
      onChange(value, filterKey)
    }, 300),
    [],
  )

  const handleSubmitToValue = () => {
    if (toLocalValue !== toValue) {
      debouncedInputOnChange('maxOccupancy', toLocalValue)
    }
  }
  const handleSubmitFromValue = () => {
    if (fromLocalValue !== fromValue) {
      debouncedInputOnChange('minOccupancy', fromLocalValue)
    }
  }

  useOnClickOutside(wrapperRef, handleSubmitToValue)
  useOnClickOutside(wrapperRef, handleSubmitFromValue)

  useEffect(() => {
    if (isSidebarFilter) {
      handleSubmitToValue()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toLocalValue, isSidebarFilter])

  useEffect(() => {
    if (isSidebarFilter) {
      handleSubmitFromValue()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromLocalValue, isSidebarFilter])

  useEffect(() => {
    if (fromValue === undefined) {
      setFromLocalValue(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromValue])

  useEffect(() => {
    if (toValue === undefined) {
      setToLocalValue(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toValue])

  const minOccupancyFilterText = fromLocalValue ? `Min: ${fromLocalValue}` : undefined
  const maxOccupancyFilterText = toLocalValue ? `Max: ${toLocalValue}` : undefined

  const filtersValues = [minOccupancyFilterText, maxOccupancyFilterText].filter(
    filter => filter !== undefined,
  )

  const handleOnClear = (index?: number) => {
    const clearFromValue = () => {
      onChange(undefined, 'minOccupancy')
      setFromLocalValue('')
    }

    const clearToValue = () => {
      onChange(undefined, 'maxOccupancy')
      setToLocalValue('')
    }

    if (index === 0) {
      clearFromValue()
    }

    if (index === 1) {
      clearToValue()
    }

    if (index === undefined || filtersValues.length === 1) {
      clearFromValue()
      clearToValue()
    }
  }

  return (
    <FilterAccordion
      title={title}
      onClearFilter={handleOnClear}
      ref={wrapperRef}
      filterValue={filtersValues}
      hasMultipleValues
      isAccordionOpen={isAccordionOpen}
      isSidebarFilter={isSidebarFilter}
    >
      <S.RangeInputWrapper>
        <TextField.SmallMasked
          placeholder="Min"
          value={fromLocalValue || ''}
          mask={percentageMask}
          onChange={({ currentTarget }) => setFromLocalValue(currentTarget.value)}
          {...textFieldProps}
        />
        <TextField.SmallMasked
          placeholder="Max"
          value={toLocalValue || ''}
          mask={percentageMask}
          onChange={({ currentTarget }) => setToLocalValue(currentTarget.value)}
          {...textFieldProps}
        />
      </S.RangeInputWrapper>
    </FilterAccordion>
  )
}
